import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { uid } from 'react-uid';
import Box from '@mui/material/Box';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableContainer from '@mui/material/TableContainer';
import TableRow from '@mui/material/TableRow';
import { Checkbox, Typography } from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { Loader } from '@alumni-ventures-group/react-shared/lib/components';
import { debounce } from 'lodash';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import TableHead from '../../common/TableHead';
import DocumentsHeadCells from './DocumentsHeadCells';
import StatusContainer from './StatusContainer';
import { getComparator, sortAlphanumerically, sortByDateOnly } from '../../../../shared/hooks/useSort';
import { downloadFileById, exportK1, exportK1sv2 } from '../../../../shared/services/apiGateway';
import LinkButton from './LinkButton';
import DocumentsTableCell from './DocumentsTableCell';
import formatDate from '../../helpers/formatDate';
import { handleFileDownload, handleDownloadK1File } from '../helpers/handleFileDownload';
import WebsocketService from '../../../../shared/services/websocketService';
import DocumentsTableFooter from './DocumentsTableFooter';

const Modal = ({
  title, description, showModal, closeModal, errorState, showModalLoading,
}) => (
  <Dialog
    open={showModal}
    onClose={() => {}}
    aria-labelledby="alert-dialog-title"
    aria-describedby="alert-dialog-description"
  >
    <DialogTitle id="alert-dialog-title" sx={{ fontSize: '1.5rem', textAlign: 'left' }}>{title}</DialogTitle>
    <DialogContent>
      <DialogContentText id="alert-dialog-description">
        {description}
      </DialogContentText>
      {showModalLoading && (
        <Box sx={{
          display: 'flex', justifyContent: 'center', alignItems: 'center', mt: 4,
        }}
        >
          <Loader />
        </Box>
      )}
    </DialogContent>
    <DialogActions>
      {errorState && (
        <Button autoFocus onClick={closeModal}>
          Close
        </Button>
      )}
    </DialogActions>
  </Dialog>
);

Modal.propTypes = {
  title: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  showModal: PropTypes.bool.isRequired,
  closeModal: PropTypes.func.isRequired,
  errorState: PropTypes.bool.isRequired,
  showModalLoading: PropTypes.bool.isRequired,
};

const errorTitle = 'An error occured';

function DocumentsTableHead({
  hideSelectionColumn,
  hideDocumentTypeColumn,
  columnsToHide,
  order,
  orderBy,
  onRequestSort,
  numSelected,
  rowCount,
  onSelectAllClick,
  alternateTitleLabel,
}) {
  let cells = DocumentsHeadCells(alternateTitleLabel);
  if (hideSelectionColumn) {
    cells = cells.filter((cell) => cell.id !== 'selection' && cell.id !== 'checkbox');
  } else if (columnsToHide) {
    cells = cells.filter((cell) => !columnsToHide.includes(cell.id));
  }
  if (hideDocumentTypeColumn) {
    cells = cells.filter((cell) => cell.id !== 'documentTypeDisplayName');
  }

  return cells && cells.length > 0 && (
    <TableHead
      hideSelectionColumn={hideSelectionColumn}
      headCells={cells}
      order={order}
      orderBy={orderBy}
      onRequestSort={onRequestSort}
      numSelected={hideSelectionColumn ? 0 : numSelected}
      rowCount={rowCount}
      onSelectAllClick={hideSelectionColumn ? null : onSelectAllClick}
    />
  );
}

DocumentsTableHead.propTypes = {
  hideSelectionColumn: PropTypes.bool,
  hideDocumentTypeColumn: PropTypes.bool, // new prop
  columnsToHide: PropTypes.arrayOf(PropTypes.string),
  order: PropTypes.string,
  orderBy: PropTypes.string,
  onRequestSort: PropTypes.func,
  numSelected: PropTypes.number,
  rowCount: PropTypes.number,
  onSelectAllClick: PropTypes.func,
  alternateTitleLabel: PropTypes.string,
};

DocumentsTableHead.defaultProps = {
  hideSelectionColumn: false,
  hideDocumentTypeColumn: false, // default value
  columnsToHide: null,
  order: 'asc',
  orderBy: '',
  onRequestSort: () => {},
  numSelected: 0,
  rowCount: 0,
  onSelectAllClick: () => {},
  alternateTitleLabel: null,
};

const DocumentsTableRows = ({
  documentsData,
  selected,
  handleClick,
  currentManagedUserId,
  hideSelectionColumn,
  hideDocumentTypeColumn, // new prop
}) => {
  const [loadingDocuments, setLoadingDocuments] = useState({});

  const handleDownload = async (fileId) => {
    try {
      setLoadingDocuments((prev) => ({ ...prev, [fileId]: true }));
      const fileDownloadUrl = await downloadFileById(
        fileId,
        currentManagedUserId ? `?userid=${currentManagedUserId}` : '',
      );
      if (fileDownloadUrl && fileDownloadUrl.fileUrl) {
        window.open(fileDownloadUrl.fileUrl, '_blank');
      }
    } catch (error) {
      console.error('Error downloading file:', error);
    } finally {
      setLoadingDocuments((prev) => ({ ...prev, [fileId]: false }));
    }
  };

  const handleDownloadK1FileDebounced = debounce(async (fileKey) => {
    const newTab = window.open('', '_blank');
    newTab.document.write('Preparing K-1.');
    const response = await exportK1(
      currentManagedUserId ? `?userid=${currentManagedUserId}` : '',
      fileKey,
    );
    if (response && response.fileUrl) {
      newTab.location.href = response.fileUrl;
    } else {
      newTab.close();
    }
  }, 375);

  return documentsData.map((row, index) => {
    const uniqueRowId = row.uniqueId;
    const isItemSelected = selected.includes(uniqueRowId);
    const isSelectable = row.status === 'Available';
    const allowSelection = !hideSelectionColumn && isSelectable;

    return (
      <React.Fragment key={uniqueRowId}>
        <TableRow
          hover={allowSelection}
          onClick={(event) => {
            if (allowSelection) {
              handleClick(event, uniqueRowId);
            }
          }}
          role="checkbox"
          aria-checked={isItemSelected}
          tabIndex={-1}
          sx={{
            backgroundColor: index % 2 === 0 ? 'table.main' : '#fff',
            '&&:last-child td, &:last-child th': { border: 0 },
            cursor: allowSelection ? 'pointer' : 'default',
          }}
        >
          {!hideSelectionColumn && (
            <DocumentsTableCell padding="checkbox" sx={{ p: '0 8px', verticalAlign: 'middle' }}>
              <Checkbox
                color="secondary"
                checked={isItemSelected}
                disabled={!allowSelection}
                inputProps={{ 'aria-labelledby': `enhanced-table-checkbox-${uniqueRowId}` }}
              />
            </DocumentsTableCell>
          )}
          <DocumentsTableCell>{row.title}</DocumentsTableCell>
          {!hideDocumentTypeColumn && (
            <DocumentsTableCell>{row.documentTypeDisplayName}</DocumentsTableCell>
          )}
          <DocumentsTableCell>
            {row?.vintageFundNames ? (
              (() => {
                const { vintageFundNames } = row;
                const displayNames = vintageFundNames.slice(0, 5);
                return (
                  <>
                    {displayNames.map((vintageFundName) => (
                      <span key={uid(vintageFundName)}>
                        {vintageFundName}
                        <br />
                      </span>
                    ))}
                    {vintageFundNames.length > 6 && <span>...</span>}
                  </>
                );
              })()
            ) : (
              row?.fundName || '-'
            )}
          </DocumentsTableCell>
          <DocumentsTableCell>
            {row.publicationDate ? formatDate(row.publicationDate) : ''}
          </DocumentsTableCell>
          <DocumentsTableCell>
            {row.status === 'Pending' ? (
              <StatusContainer status={row.status}>Not Yet Available</StatusContainer>
            ) : (
              <LinkButton
                variant="text"
                loading={
                  row.fileKey
                    ? loadingDocuments?.[row.fileKey]
                    : loadingDocuments?.[row.id]
                }
                onClick={(e) => {
                  e.stopPropagation();
                  if (row.fileKey) {
                    handleDownloadK1FileDebounced(row.fileKey);
                  } else {
                    handleFileDownload(row.id, currentManagedUserId, setLoadingDocuments);
                  }
                }}
                sx={{ minWidth: 'auto' }}
              >
                View
                <OpenInNewIcon className="openIcon" sx={{ fontSize: 16, ml: 1.5 }} />
              </LinkButton>
            )}
          </DocumentsTableCell>
        </TableRow>
      </React.Fragment>
    );
  });
};

DocumentsTableRows.propTypes = {
  documentsData: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    title: PropTypes.string.isRequired,
    fundName: PropTypes.string,
    vintageFundNames: PropTypes.arrayOf(PropTypes.string),
    publicationDate: PropTypes.string,
    status: PropTypes.oneOf(['Pending', 'Available']),
    fileKey: PropTypes.string,
    uniqueId: PropTypes.string,
    documentTypeDisplayName: PropTypes.string,
  })).isRequired,
  currentManagedUserId: PropTypes.number,
  hideSelectionColumn: PropTypes.bool,
  hideDocumentTypeColumn: PropTypes.bool, // new prop
  selected: PropTypes.arrayOf(PropTypes.string).isRequired,
  handleClick: PropTypes.func.isRequired,
};

DocumentsTableRows.defaultProps = {
  currentManagedUserId: null,
  hideSelectionColumn: false,
  hideDocumentTypeColumn: false, // default value
};

export default function DocumentsTable({
  documentsData,
  currentManagedUserId,
  isLoading,
  isError,
  hideSelectionColumn,
  hideDocumentTypeColumn, // new prop
  alternateTitleLabel,
  selectAllRowsOnLoad,
}) {
  const [order, setOrder] = useState('desc');
  const [orderBy, setOrderBy] = useState('publicationDate');
  const [selected, setSelected] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [modalText, setModalText] = useState([]);
  const [modalLoading, setModalLoading] = useState(false);

  const documentsWithUniqueId = React.useMemo(
    () => documentsData.map((row) => ({
      ...row,
      uniqueId: uid(row),
    })),
    [documentsData],
  );

  const selectableRowCount = React.useMemo(
    () => documentsWithUniqueId.filter((n) => n.status === 'Available').length,
    [documentsWithUniqueId],
  );

  useEffect(() => {
    if (selectAllRowsOnLoad) {
      const allSelectable = documentsWithUniqueId
        .filter((n) => n.status === 'Available')
        .map((n) => n.uniqueId);
      setSelected(allSelectable);
    }
  }, [documentsWithUniqueId, selectAllRowsOnLoad]);

  const websocketRef = useRef();
  const timeoutRef = useRef();
  const websocketTimeout = 60000;
  const [isWebsocketConnected, setIsWebsocketConnected] = useState(false);

  const closeDownloadModal = () => {
    setShowModal(false);
    setModalLoading(false);
    setModalText([]);
    clearTimeout(timeoutRef.current);
    if (websocketRef.current) {
      websocketRef.current.disconnect();
    }
    setIsWebsocketConnected(false);
  };

  const handleGetConnectionIdResponse = async (connectionId) => {
    const selectedDocs = documentsWithUniqueId.filter((doc) => selected.includes(doc.uniqueId));
    const fileIds = [];
    const fileKeys = [];
    const years = [];
    selectedDocs.forEach((doc) => {
      if (doc.fileKey) {
        fileKeys.push(doc.fileKey);
      } else {
        fileIds.push(doc.id);
      }
      if (doc.year && !years.includes(doc.year)) {
        years.push(doc.year);
      }
    });

    try {
      const response = await exportK1sv2(
        currentManagedUserId ? `?userid=${currentManagedUserId}` : '',
        connectionId,
        fileKeys,
        fileIds,
        years,
      );
      if (response && response.fileUrl) {
        window.open(response.fileUrl, '_blank');
        closeDownloadModal();
      }
    } catch (error) {
      console.error('Error exporting K-1s:', error);
      closeDownloadModal();
    }
  };

  const handleWebsocketResponse = async (response) => {
    const message = JSON.parse(response.data);
    setIsWebsocketConnected(true);
    if (message.action === 'getConnectionId') {
      await handleGetConnectionIdResponse(message.connectionId);
    } else if (message.action === 'k1ExportComplete') {
      const { data } = message;
      if (data.fileUrl) {
        window.open(data.fileUrl, '_blank');
        closeDownloadModal();
      } else {
        closeDownloadModal();
      }
    }
  };

  const closeDownloadModalAndReset = () => {
    closeDownloadModal();
  };

  const requestDownload = async () => {
    if (selected.length === 0) { return; }
    setShowModal(true);
    setModalText([
      'Preparing K-1s for download.',
      'Please do not navigate away from or refresh the page.',
    ]);
    setModalLoading(true);
    if (isWebsocketConnected) { return; }
    timeoutRef.current = setTimeout(() => {
      closeDownloadModalAndReset();
    }, websocketTimeout);
    websocketRef.current = new WebsocketService(
      process.env.K1_EXPORT_WEBSOCKET_URL,
      () => {
        websocketRef.current.sendMessage(JSON.stringify({ action: 'getConnectionId' }));
      },
      () => {
        clearTimeout(timeoutRef.current);
      },
      closeDownloadModalAndReset,
      handleWebsocketResponse,
    );
  };

  const handleDownloadSelected = async () => {
    if (selected.length > 1) {
      requestDownload();
    } else if (selected.length === 1) {
      const doc = documentsWithUniqueId.find((d) => d.uniqueId === selected[0]);
      if (doc && doc.fileKey) {
        handleDownloadK1File(doc.fileKey, currentManagedUserId);
      } else {
        handleFileDownload(doc.id, currentManagedUserId);
      }
    }
  };

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const newSelected = documentsWithUniqueId
        .filter((n) => n.status === 'Available')
        .map((n) => n.uniqueId);
      setSelected(newSelected);
    } else {
      setSelected([]);
    }
  };

  const handleClick = (event, uniqueRowId) => {
    const selectedIndex = selected.indexOf(uniqueRowId);
    let newSelected = [];
    if (selectedIndex === -1) {
      newSelected = [...selected, uniqueRowId];
    } else if (selectedIndex === 0) {
      newSelected = selected.slice(1);
    } else if (selectedIndex === selected.length - 1) {
      newSelected = selected.slice(0, selectedIndex);
    } else {
      newSelected = [
        ...selected.slice(0, selectedIndex),
        ...selected.slice(selectedIndex + 1),
      ];
    }
    setSelected(newSelected);
  };

  const handleRequestSort = (event, property) => {
    const isAscending = orderBy === property && order === 'asc';
    setOrder(isAscending ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const sortedData = React.useMemo(() => {
    if (orderBy === 'publicationDate') {
      return documentsWithUniqueId.sort(
        getComparator(order, orderBy, sortByDateOnly, 'title', 'asc', sortAlphanumerically),
      );
    }
    if (orderBy === 'vintageFundNames') {
      return documentsWithUniqueId.sort(
        getComparator(order, 'sortableFundValue', sortAlphanumerically, 'publicationDate', 'desc', sortByDateOnly),
      );
    }

    return documentsWithUniqueId.sort(
      getComparator(order, orderBy, sortAlphanumerically, 'publicationDate', 'desc', sortByDateOnly),
    );
  }, [documentsWithUniqueId, order, orderBy]);

  return (
    <>
      <Box sx={{ width: '100%' }}>
        <TableContainer
          sx={{
            borderWidth: '1px',
            borderStyle: 'solid',
            borderColor: 'border.dark',
            borderRadius: 2,
            mb: 4,
            mt: 1.5,
          }}
        >
          <Table aria-labelledby="documents-table" sx={{ minWidth: 620 }}>
            <DocumentsTableHead
              hideSelectionColumn={hideSelectionColumn}
              hideDocumentTypeColumn={hideDocumentTypeColumn} // pass new prop
              order={order}
              orderBy={orderBy}
              onRequestSort={handleRequestSort}
              numSelected={selected.length}
              rowCount={selectableRowCount}
              onSelectAllClick={hideSelectionColumn ? null : handleSelectAllClick}
              alternateTitleLabel={alternateTitleLabel}
            />
            <TableBody>
              {(!documentsWithUniqueId || documentsWithUniqueId.length === 0 || isLoading || isError) ? (
                <TableRow>
                  <DocumentsTableCell colSpan={5} sx={{ border: 'none', height: '280px' }}>
                    <Box sx={{
                      display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%',
                    }}
                    >
                      {isLoading ? (
                        <Loader />
                      ) : (
                        <Typography variant="tableMessage">
                          {isError ? 'Oops, something went wrong. Please try again.' : 'No Documents to Display'}
                        </Typography>
                      )}
                    </Box>
                  </DocumentsTableCell>
                </TableRow>
              ) : (
                <DocumentsTableRows
                  documentsData={sortedData}
                  selected={selected}
                  handleClick={handleClick}
                  currentManagedUserId={currentManagedUserId}
                  hideSelectionColumn={hideSelectionColumn}
                  hideDocumentTypeColumn={hideDocumentTypeColumn} // pass new prop
                />
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
      {selected.length > 0 && (
        <DocumentsTableFooter
          selectedCount={selected.length}
          onUnselectAll={() => setSelected([])}
          onDownload={handleDownloadSelected}
        />
      )}
      {showModal && (
        <Modal
          title={modalText[0]}
          description={modalText[1]}
          showModal={showModal}
          closeModal={closeDownloadModal}
          errorState={modalText[0] === errorTitle}
          showModalLoading={modalLoading}
        />
      )}
    </>
  );
}

DocumentsTable.propTypes = {
  documentsData: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    title: PropTypes.string.isRequired,
    fileName: PropTypes.string,
    fundName: PropTypes.string,
    vintageFundNames: PropTypes.arrayOf(PropTypes.string),
    publicationDate: PropTypes.string,
    status: PropTypes.oneOf(['Pending', 'Available']),
    year: PropTypes.number,
    fileKey: PropTypes.string,
    documentType: PropTypes.string,
  })).isRequired,
  currentManagedUserId: PropTypes.number,
  isLoading: PropTypes.bool,
  isError: PropTypes.bool,
  hideSelectionColumn: PropTypes.bool,
  hideDocumentTypeColumn: PropTypes.bool,
  alternateTitleLabel: PropTypes.string,
  selectAllRowsOnLoad: PropTypes.bool,
};

DocumentsTable.defaultProps = {
  currentManagedUserId: null,
  isLoading: false,
  isError: false,
  hideSelectionColumn: false,
  hideDocumentTypeColumn: false, // default value
  alternateTitleLabel: null,
  selectAllRowsOnLoad: false,
};
